@file:Suppress("MagicNumber")

package com.nonopn.skydome.common.app.pages.keraunic

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.nonopn.skydome.common.app.pages.pdf.KeraunicHourlyData
import com.nonopn.skydome.common.app.pages.pdf.KeraunicMonthlyData
import com.nonopn.skydome.common.app.pages.pdf.KeraunicReportData
import com.nonopn.skydome.common.app.pages.pdf.KeraunicReportText
import com.nonopn.skydome.common.app.pages.pdf.KeraunicRiskLevel
import com.nonopn.skydome.common.app.utils.fmt

// On-screen rendering of PDF pages 6 to 10 (effects, monthly and hourly charts), same data as the PDF

internal fun riskLabel(text: KeraunicReportText, level: KeraunicRiskLevel): String = when (level) {
    KeraunicRiskLevel.HIGH -> text.recommendations.riskHigh
    KeraunicRiskLevel.MODERATE -> text.recommendations.riskModerate
    KeraunicRiskLevel.LOW -> text.recommendations.riskLow
}

// ─── Page 6 — possible effects & wider context ─────────────────────────────

private data class Effect(val title: String, val description: String, val border: Color, val background: Color, val titleColor: Color)

@Composable
internal fun KeraunicEffectsSection(data: KeraunicReportData, text: KeraunicReportText) {
    val e = text.effects
    val riskNote = if (data.risk.level == KeraunicRiskLevel.LOW) e.synthesisLowNote else e.synthesisRiskNote
    ReportCard {
        ReportPageHeader(text.plain(e.header))
        EffectsGroup(
            "\u26a1 ${text.plain(e.directTitle)}",
            listOf(
                Effect("\uD83D\uDD25 ${text.plain(e.fire)}", text.plain(e.fireDescription), ReportColors.Amber, ReportColors.KeyPointsBg, ReportColors.Orange),
                Effect(
                    "\uD83D\uDCA5 ${text.plain(e.structuralDamage)}", text.plain(e.structuralDamageDescription),
                    ReportColors.Amber, ReportColors.KeyPointsBg, ReportColors.Orange
                ),
                Effect(
                    "\u26a0\uFE0F ${text.plain(e.lifeSafety)}", text.plain(e.lifeSafetyDescription),
                    ReportColors.Orange, Color(0xFFFFE0B2), Color(0xFFB71C1C)
                ),
            )
        )
        EffectsGroup(
            "\u301c ${text.plain(e.indirectTitle)}",
            listOf(
                Effect("\u26a1 ${text.plain(e.lempSurges)}", text.plain(e.lempDescription), ReportColors.Blue, ReportColors.NetworkBg, Color(0xFF1A4A8A)),
                Effect(
                    "\uD83E\uDD16 ${text.plain(e.automation)}", text.plain(e.automationDescription),
                    ReportColors.Blue, ReportColors.NetworkBg, Color(0xFF1A4A8A)
                ),
            )
        )
        Column(
            Modifier.fillMaxWidth()
                .clip(RoundedCornerShape(10.dp))
                .background(Color(0xFFE8F5E9))
                .border(1.5.dp, ReportColors.Green, RoundedCornerShape(10.dp))
                .padding(12.dp),
            verticalArrangement = Arrangement.spacedBy(4.dp)
        ) {
            Text(text.plain(e.synthesis), fontSize = 14.sp, fontWeight = FontWeight.Bold, color = Color(0xFF1B5E20))
            Text(
                text.fill(
                    text.plain(e.synthesisText),
                    data.maxKa.fmt(0),
                    data.zone1.totalCount.toString(),
                    data.zone2.totalCount.toString(),
                    riskLabel(text, data.risk.level)
                ) + " " + text.plain(riskNote),
                fontSize = 13.sp,
                color = Color(0xFF1B5E20),
                lineHeight = 19.sp
            )
        }
    }
}

@Composable
private fun EffectsGroup(title: String, effects: List<Effect>) {
    Text(title, fontSize = 14.sp, fontWeight = FontWeight.Bold, color = ReportColors.Text)
    BoxWithConstraints(Modifier.fillMaxWidth()) {
        if (maxWidth < 560.dp) {
            Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                effects.forEach { EffectCard(Modifier.fillMaxWidth(), it) }
            }
        } else {
            Row(horizontalArrangement = Arrangement.spacedBy(10.dp)) {
                effects.forEach { EffectCard(Modifier.weight(1f), it) }
            }
        }
    }
}

@Composable
private fun EffectCard(modifier: Modifier, effect: Effect) {
    Column(
        modifier.clip(RoundedCornerShape(10.dp))
            .background(effect.background)
            .border(1.5.dp, effect.border, RoundedCornerShape(10.dp))
            .padding(12.dp),
        verticalArrangement = Arrangement.spacedBy(4.dp)
    ) {
        Text(effect.title.uppercase(), fontSize = 13.sp, fontWeight = FontWeight.Bold, color = effect.titleColor)
        Text(effect.description, fontSize = 13.sp, color = ReportColors.Text, lineHeight = 18.sp)
    }
}

// ─── Pages 7-10 — monthly and hourly charts ────────────────────────────────

@Composable
internal fun KeraunicMonthlyActivitySection(data: KeraunicReportData, text: KeraunicReportText) {
    val c = text.charts
    ReportCard {
        ReportPageHeader(text.plain(c.monthlyAndIntensityDistribution))
        ReportSectionTitle("\uD83D\uDCC5 ${text.plain(c.monthlyActivity)}")
        ChartTitle("${text.plain(c.zone)} 0\u20131 km", "(${text.plain(c.strikesPerMonth)})", ReportColors.Blue)
        ChartBox(text) { GroupedBarChart(data.monthly1Km.map { text.month(it.month) to (it.negCount to it.posCount) }, text) }
        ChartTitle("${text.plain(c.zone)} 1\u20132 km", "(${text.plain(c.strikesPerMonth)})", ReportColors.Red)
        ChartBox(text) { GroupedBarChart(data.monthly2Km.map { text.month(it.month) to (it.negCount to it.posCount) }, text) }
    }
}

@Composable
internal fun KeraunicMonthlyIntensitySection(data: KeraunicReportData, text: KeraunicReportText) {
    val c = text.charts
    ReportCard {
        ReportPageHeader(text.plain(c.monthlyIntensityHeader))
        ReportSectionTitle("\uD83D\uDCC8 ${text.plain(c.averageMonthlyIntensity)}")
        ReportBody(text.plain(c.monthlyIntensityDescription))
        ChartTitle("${text.plain(c.zone)} 0\u20131 km", "(${text.plain(c.averageKaPerMonth)})", ReportColors.Blue)
        ChartBox(text) { LineChart(data.monthly1Km.monthlyAverages(text), text) }
        ChartTitle("${text.plain(c.zone)} 1\u20132 km", "(${text.plain(c.averageKaPerMonth)})", ReportColors.Red)
        ChartBox(text) { LineChart(data.monthly2Km.monthlyAverages(text), text) }
    }
}

private fun List<KeraunicMonthlyData>.monthlyAverages(text: KeraunicReportText) =
    map { text.month(it.month) to (it.negAvgKa to it.posAvgKa) }

@Composable
internal fun KeraunicHourlyDistributionSection(hourly: List<KeraunicHourlyData>, text: KeraunicReportText) {
    val c = text.charts
    ReportCard {
        ReportPageHeader(text.plain(c.hourlyDistributionHeader))
        ReportSectionTitle("\u23F1 ${text.plain(c.hourlyDistribution)}")
        ChartTitle(text.plain(c.allImpactsAroundSite), "(${text.plain(c.combinedZones)})", ReportColors.Blue)
        ChartBox(text) { GroupedBarChart(hourly.map { text.hour(it.hour) to (it.negCount to it.posCount) }, text) }
    }
}

@Composable
internal fun KeraunicHourlyIntensitySection(hourly: List<KeraunicHourlyData>, text: KeraunicReportText) {
    val c = text.charts
    ReportCard {
        ReportPageHeader(text.plain(c.monthlyHourlyIntensityHeader))
        ReportSectionTitle("\uD83D\uDCC8 ${text.plain(c.averageHourlyIntensity)}")
        ReportBody(text.plain(c.dailyProfile))
        ChartTitle(text.plain(c.allImpactsAroundSite), "(${text.plain(c.averageKaPerHour)})", ReportColors.Blue)
        ChartBox(text) { LineChart(hourly.map { text.hour(it.hour) to (it.negAvgKa to it.posAvgKa) }, text) }
    }
}

@Composable
private fun ChartTitle(title: String, unit: String, color: Color) {
    Row(verticalAlignment = Alignment.CenterVertically) {
        Text(title, fontSize = 13.sp, fontWeight = FontWeight.SemiBold, color = color)
        Text("  $unit", fontSize = 12.sp, color = ReportColors.Muted)
    }
}

@Composable
private fun ChartBox(text: KeraunicReportText, content: @Composable () -> Unit) {
    Column(
        Modifier.fillMaxWidth()
            .clip(RoundedCornerShape(10.dp))
            .background(ReportColors.SurfaceAlt)
            .border(1.dp, ReportColors.Border, RoundedCornerShape(10.dp))
            .padding(10.dp),
        verticalArrangement = Arrangement.spacedBy(6.dp)
    ) {
        Row(horizontalArrangement = Arrangement.spacedBy(14.dp)) {
            LegendItem(ReportColors.Blue, text.plain(text.charts.negativeSeries))
            LegendItem(ReportColors.Red, text.plain(text.charts.positiveSeries))
        }
        content()
    }
}

@Composable
private fun LegendItem(color: Color, label: String) {
    Row(verticalAlignment = Alignment.CenterVertically) {
        Box(Modifier.size(9.dp).clip(RoundedCornerShape(2.dp)).background(color))
        Text(" $label", fontSize = 11.sp, color = Color(0xFF4A5A7A))
    }
}

@Composable
private fun NotAvailable(text: KeraunicReportText) {
    Text(
        text.plain(text.charts.notAvailable),
        modifier = Modifier.fillMaxWidth().padding(vertical = 24.dp),
        fontSize = 13.sp,
        color = ReportColors.Light,
        textAlign = TextAlign.Center
    )
}

// Every label up to 12 points, one in two beyond (24 hours on a phone)
private fun showLabel(index: Int, count: Int) = count <= 12 || index % 2 == 0

// CG- and CG+ bars side by side for each label, with the value above the bar (as in the PDF)
@Composable
private fun GroupedBarChart(points: List<Pair<String, Pair<Int, Int>>>, text: KeraunicReportText) {
    if (points.isEmpty()) {
        NotAvailable(text)
        return
    }
    val maxVal = points.maxOf { maxOf(it.second.first, it.second.second) }.coerceAtLeast(1)
    Row(Modifier.fillMaxWidth().height(150.dp), horizontalArrangement = Arrangement.spacedBy(2.dp)) {
        points.forEachIndexed { index, (label, values) ->
            Column(Modifier.weight(1f).fillMaxHeight(), horizontalAlignment = Alignment.CenterHorizontally) {
                Row(Modifier.weight(1f).fillMaxWidth(), horizontalArrangement = Arrangement.Center, verticalAlignment = Alignment.Bottom) {
                    Bar(Modifier.weight(1f).widthIn(max = 10.dp), values.first, maxVal, ReportColors.Blue)
                    Spacer(Modifier.width(1.dp))
                    Bar(Modifier.weight(1f).widthIn(max = 10.dp), values.second, maxVal, ReportColors.Red)
                }
                Text(
                    if (showLabel(index, points.size)) label else "",
                    fontSize = 8.sp,
                    color = ReportColors.Muted,
                    maxLines = 1,
                    softWrap = false,
                    modifier = Modifier.padding(top = 2.dp)
                )
            }
        }
    }
}

@Composable
private fun Bar(modifier: Modifier, value: Int, maxVal: Int, color: Color) {
    Column(modifier.fillMaxHeight(), horizontalAlignment = Alignment.CenterHorizontally, verticalArrangement = Arrangement.Bottom) {
        if (value > 0) {
            if (value > maxVal * 0.1) {
                Text("$value", fontSize = 7.sp, color = color, maxLines = 1, softWrap = false)
            }
            // Reserve room for the value label so the highest bar never overflows
            val fraction = (value.toFloat() / maxVal * 0.85f).coerceIn(0.02f, 0.85f)
            Box(
                Modifier.fillMaxWidth().fillMaxHeight(fraction)
                    .clip(RoundedCornerShape(topStart = 2.dp, topEnd = 2.dp))
                    .background(color)
            )
        }
    }
}

// CG- and CG+ average intensity lines (kA), same scale for both series
@Composable
private fun LineChart(points: List<Pair<String, Pair<Double, Double>>>, text: KeraunicReportText) {
    if (points.isEmpty()) {
        NotAvailable(text)
        return
    }
    val maxVal = points.maxOf { maxOf(it.second.first, it.second.second) }.takeIf { it > 0.0 } ?: 1.0
    Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.Top) {
        Column(Modifier.height(130.dp).width(34.dp), verticalArrangement = Arrangement.SpaceBetween) {
            Text("${maxVal.fmt(0)} kA", fontSize = 8.sp, color = ReportColors.Muted, maxLines = 1)
            Text("0", fontSize = 8.sp, color = ReportColors.Muted)
        }
        Column(Modifier.weight(1f)) {
            Canvas(Modifier.fillMaxWidth().height(130.dp)) {
                val step = size.width / points.size
                fun y(v: Double) = size.height - (v / maxVal * size.height * 0.92).toFloat()
                drawLine(Color(0xFFDDDDDD), Offset(0f, size.height), Offset(size.width, size.height), 1f)
                listOf(ReportColors.Blue to { p: Pair<Double, Double> -> p.first }, ReportColors.Red to { p: Pair<Double, Double> -> p.second })
                    .forEach { (color, pick) ->
                        val offsets = points.mapIndexed { i, (_, v) -> Offset(step * i + step / 2, y(pick(v))) }
                        offsets.zipWithNext { a, b -> drawLine(color, a, b, strokeWidth = 2.5.dp.toPx()) }
                        offsets.forEach { drawCircle(color, radius = 3.dp.toPx(), center = it) }
                    }
            }
            Row(Modifier.fillMaxWidth()) {
                points.forEachIndexed { index, (label, _) ->
                    Text(if (showLabel(index, points.size)) label else "", softWrap = false, modifier = Modifier.weight(1f), fontSize = 8.sp, color = ReportColors.Muted, textAlign = TextAlign.Center, maxLines = 1)
                }
            }
        }
    }
}
